import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import type {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "../form-field";

// eslint-disable-next-line react-refresh/only-export-components
export const ATTACHMENT_FORM_FIELD_TYPE = "attachment" as const;

export type AttachmentFormFieldDefinition = BaseFormFieldDefinition<
  typeof ATTACHMENT_FORM_FIELD_TYPE
> & {
  type: typeof ATTACHMENT_FORM_FIELD_TYPE;
  entityId?: string;
  onFetchAttachments?: (entityId: string) => Promise<Attachment[]>;
  onUploadAttachment?: (
    entityId: string,
    file: File
  ) => Promise<Attachment | null>;
  onDeleteAttachment?: (id: string) => Promise<boolean>;
  onDownloadAttachment?: (id: string) => Promise<void>;
};

interface Attachment {
  id: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
  comment?: string;
}

export function AttachmentFormField({
  disabled,
  errorMessage,
  entityId,
  name,
  register,
  required,
  onFetchAttachments,
  onUploadAttachment,
  onDeleteAttachment,
  onDownloadAttachment,
}: AttachmentFormFieldDefinition & AdditionalFormFieldProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { onChange } = register(name, { required });

  const loadAttachments = useCallback(async () => {
    if (!entityId || !onFetchAttachments) return;

    try {
      const data = await onFetchAttachments(entityId);
      setAttachments(data);
      // Update the form value when attachments are loaded
      onChange({
        target: {
          name: name,
          value: data,
        } as unknown as HTMLInputElement,
      } as React.ChangeEvent<HTMLInputElement>);
    } catch (error) {
      console.error("Failed to load attachments:", error);
    }
  }, [entityId, onFetchAttachments, onChange, name]);

  // Load attachments when component mounts
  useEffect(() => {
    if (entityId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadAttachments();
    }
  }, [entityId, loadAttachments]);

  const handleFileUpload = async (file: File) => {
    if (!entityId || !onUploadAttachment) return;

    setUploading(true);
    try {
      const result = await onUploadAttachment(entityId, file);
      if (result) {
        await loadAttachments();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleDownload = async (id: string) => {
    if (onDownloadAttachment) {
      await onDownloadAttachment(id);
    }
  };

  const handleDelete = async (id: string) => {
    if (onDeleteAttachment) {
      const success = await onDeleteAttachment(id);
      if (success) {
        await loadAttachments();
      }
    }
  };

  return (
    <FormControl error={!!errorMessage} fullWidth>
      <Button
        variant="text"
        color="primary"
        startIcon={<UploadIcon />}
        disabled={disabled}
        onClick={() => setDialogOpen(true)}
        sx={{
          justifyContent: "flex-start",
          mb: 1,
          p: 0,
          minWidth: 0,
          textTransform: "none",
          color: "#1565c0 !important",
          "& .MuiButton-startIcon": {
            color: "#1565c0",
            mr: 0.5,
          },
          "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
        }}
      >
        Upload document
      </Button>

      {attachments.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {attachments.map((attachment) => (
            <Box
              key={attachment.id}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                gap: 1,
              }}
            >
              <Link
                component="button"
                onClick={() => handleDownload(attachment.id)}
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "14px",
                  flex: 1,
                  textAlign: "left",
                }}
              >
                {attachment.originalName}
              </Link>
              <IconButton
                onClick={() => handleDelete(attachment.id)}
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    color: "#333",
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Document Upload
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 6,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "#999" },
              transition: "background-color 150ms, border-color 150ms",
              backgroundColor: isDragging
                ? "rgba(25,118,210,0.04)"
                : "transparent",
              borderColor: isDragging ? "#1976d2" : undefined,
            }}
            onClick={() => document.getElementById("file-input")?.click()}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "#1976d2", mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              Drag & drop file or select from computer
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Supported file types: .xls(x), .ppt(x), .doc(x), .pdf, .jp(e)g,
              .png, .txt, .md
            </Typography>
          </Box>

          <input
            id="file-input"
            type="file"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                handleFileUpload(e.target.files[0]);
              }
            }}
            style={{ display: "none" }}
            accept=".xlsx,.xls,.pptx,.ppt,.docx,.doc,.pdf,.jpg,.jpeg,.png,.txt,.md"
          />

          {uploading && (
            <Typography sx={{ mt: 2, textAlign: "center" }}>
              Uploading...
            </Typography>
          )}

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
          >
            <Button
              variant="contained"
              onClick={() => setDialogOpen(false)}
              sx={{ color: "#fff" }}
            >
              CANCEL
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => document.getElementById("file-input")?.click()}
              disabled={uploading}
            >
              UPLOAD
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}
